import { useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useLocation } from 'react-use';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import {
  CloudApp,
  useAllAccounts,
  useEnvironments,
  usePermissions,
  useProject,
  useProjects,
} from '@dbt-labs/app-nav';
import { Explorer, useAccount } from '@dbt-labs/dbt-explorer';
import { useAmplitudeContextChange } from '@dbt-labs/integrations/amplitude';
import { useLaunchDarklyContextChange } from '@dbt-labs/integrations/feature-flags/hooks';
import { usePendoContextChange } from '@dbt-labs/integrations/pendo';
import { useSnowplowPageViewTracking } from '@dbt-labs/integrations/snowplow';
import { ErrorBoundary, useTheme } from '@dbt-labs/sourdough';

import { NotFoundPage } from '../../../packages/dbt-explorer/src/components/ErrorScreens/NotFound';
import { useLinkToDbtCloud } from '../../../packages/dbt-explorer/src/hooks/links';
import { AppThemeProvider } from './AppThemeProvider';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (numFailures, error) => {
        if (numFailures >= 3) return false;
        // disable retries for 404s
        // @ts-ignore
        return error?.response?.status !== 404;
      },
    },
  },
});

/**
 * Since we are outside of the react-router context,
 * we need to manually parse the URL to get relevant information.
 */
const useManuallyParsedUrl = () => {
  const location = useLocation();
  const pathName = location.pathname;

  return useMemo(() => {
    // routes are expected like this: `/explore/1/projects/672/`
    const parts = pathName?.split('/') ?? [];
    const accountId =
      parts[1] === 'explore' && parts[2] ? parseInt(parts[2]) : undefined;
    const projectId =
      parts[3] === 'projects' && parts[4] ? parseInt(parts[4]) : undefined;
    const environmentType =
      parts[5] === 'environments' && parts[6] ? parts[6] : undefined;
    return {
      projectId,
      accountId,
      environmentType,
    };
  }, [pathName]);
};

function getBaseUrl() {
  const url = window.location.pathname;
  if (url.startsWith('/explore')) {
    return 'explore';
  }
  return undefined;
}

export const App = () => {
  const baseUrl = getBaseUrl() ?? undefined;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppThemeProvider>
          <style>
            {`
              /* Workaround to enable independent scrolling by overriding CloudApp min-h-dvh */
              .cloud-app-height-override [class*="min-h-dvh"] {
                min-height: 0 !important;
                height: 100% !important;
              }
              .cloud-app-height-override .flex[class*="min-h-dvh"] {
                min-height: 0 !important;
                height: 100% !important;
              }
            `}
          </style>
          <BrowserRouter basename={baseUrl}>
            <Routes>
              <Route
                path="*"
                element={
                  <div className="h-screen w-screen" data-testid="app-root">
                    <AppWithQueryContext />
                  </div>
                }
              />
            </Routes>
          </BrowserRouter>
        </AppThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const AppWithQueryContext = () => {
  const { accountId, projectId } = useManuallyParsedUrl();
  const accountsQuery = useAllAccounts();
  const activeProject = useProject(accountId, projectId);
  const dbtCloudProjects = useProjects(accountId).data?.map((p) => ({
    name: p.name,
    id: p.id!,
  }));
  const prodEnvironmentQuery = useEnvironments(accountId, projectId, 1, [], {
    deployment_type: 'production',
  });
  const stgEnvironmentQuery = useEnvironments(accountId, projectId, 1, [], {
    deployment_type: 'staging',
  });
  const prodEnvironment = useMemo(() => {
    const environments = prodEnvironmentQuery.data?.pages.flatMap(
      (page) => page.data,
    ) || [undefined];

    return environments[0];
  }, [prodEnvironmentQuery.data]);
  const stgEnvironment = useMemo(() => {
    const environments = stgEnvironmentQuery.data?.pages.flatMap(
      (page) => page.data,
    ) || [undefined];

    return environments[0];
  }, [stgEnvironmentQuery.data]);

  useSnowplowPageViewTracking();
  const accountPermissions = usePermissions(accountId, accountId);
  const maybeProjectPermissions = usePermissions(accountId, projectId);
  const hasDeveloperLicense =
    !!maybeProjectPermissions.permissions.develop_access ||
    !!accountPermissions.permissions.develop_access;
  const isAccountAdmin = !!accountPermissions.permissions.account_write;

  const accountQuery = useAccount(accountId ?? 0);
  usePendoContextChange(accountQuery.data || {}, activeProject.data || {});
  useLaunchDarklyContextChange(accountQuery.data || {});
  useAmplitudeContextChange(accountQuery.data ?? {}, activeProject.data ?? {});
  const accountName = accountQuery.data?.name;
  const getProjectLink = useLinkToDbtCloud('projectDashboard');
  const getAccountLink = useLinkToDbtCloud('accountHome');
  const { theme } = useTheme();

  const content: React.ReactNode = !accountId ? (
    <NotFoundPage />
  ) : (
    <div className="z-0 h-full w-full">
      <Explorer
        currentAccountId={accountId}
        dbtCloudAccountName={accountName}
        dbtCloudProjects={dbtCloudProjects}
        currentProjectId={projectId}
        defaultEnvironmentId={
          prodEnvironment?.id || stgEnvironment?.id || 'NO_DEFAULT_ENVIRONMENT'
        }
        reactQueryClient={queryClient}
        hasDeveloperLicense={hasDeveloperLicense}
        isAdmin={isAccountAdmin}
        appContext="dbt-cloud"
        theme={theme}
      />
    </div>
  );

  return (
    <div className="h-screen w-screen overflow-hidden">
      <div className="cloud-app-height-override h-full" style={{ maxHeight: '100vh' }}>
        <CloudApp
          accountId={accountId}
          projectId={projectId}
          features={undefined}
          onProjectChange={(projectId) => {
            if (!accountId) return;
            window.location.href = getProjectLink({
              accountId,
              projectId,
            });
          }}
          onAccountChange={(accountId) => {
            const toAccount = accountsQuery.data?.find((a) => a.id === accountId);
            const isRegional = toAccount && toAccount.regional && toAccount.static_url;
            const currentHostname = window.location.hostname;
            const isLocalDev = currentHostname === 'localhost';
            const isOnDifferentSubdomain =
              toAccount &&
              new URL(toAccount.static_url ?? window.location.href).hostname !==
                currentHostname;

            if ((isRegional || isOnDifferentSubdomain) && !isLocalDev) {
              const staticURL = (toAccount.static_url ?? '').replace(/\/$/, '');
              const external = getAccountLink({ accountId, domain: staticURL });
              window.location.href = external;
            } else {
              window.location.href = getAccountLink({
                accountId,
                domain: window.location.origin,
              });
            }
          }}
          linkResolver={(props) => {
            const url = props.to.toString();
            const isInternal = url.includes('/explore');
            return {
              ...props,
              isInternal,
              to: isInternal ? url.replace('/explore', '') : url,
            };
          }}
          enableAccountChecks={true}
        >
          {content}
        </CloudApp>
      </div>
    </div>
  );
};
